import {
    AlertCircle,
    ArrowLeftRight,
    Check,
    Loader2,
    ShieldCheck,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    anchorLabel,
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import { describeErrorText } from '@/lib/errors';
import { cn, shortenAddress } from '@/lib/utils';

import type { EoaProvisioningPhase } from '../../eoaProvisioningCeremony';
import type { ProvisioningLeg } from '../../hooks/useEoaProvisioning';
import type { ProvisioningFailure } from '../../services/provisioningFailure';
import type { WalletMismatch } from '../../services/walletMismatch';
import ProvisioningProgress from '../ProvisioningProgress';

interface ProvisionStepProps {
    phase: EoaProvisioningPhase | null;
    /** Authorization anchor the account is being created on; 25 by default. */
    anchorChainId?: AnchorChainId;
    /** Set whenever the user has something to act on; null while healthy. */
    failure: ProvisioningFailure | null;
    /** Which checklist row the failure belongs to. */
    failedLeg: ProvisioningLeg | null;
    isRunning: boolean;
    hasStarted: boolean;
    /** The backend is running the detached HyperEVM creation right now. */
    detachedProvisioning: boolean;
    authorizationWalletReady: boolean;
    tradingWalletReady: boolean;
    /**
     * Association run: the social login this account is being created for. The
     * anchor leg carries it, and the HyperEVM leg asks for nothing at all.
     */
    socialProviderName?: string | null;
    /**
     * The wallet is live on a different account than the login belongs to, so
     * the preflight refused to run the ceremony. Null while they agree.
     */
    mismatch?: WalletMismatch | null;
    onStart: () => void;
    /** Leaves the signed-in account and continues as the connected one. */
    onSwitchAccount: () => void | Promise<void>;
    /** Wins the lane back after it expired; routes on to the queue step. */
    onRejoin: () => void;
    onContinue: () => void;
    /** Continue is waiting on the wallet's signature for the first session. */
    isContinuing?: boolean;
    /** The last Continue attempt failed; pressing Continue again re-asks. */
    continueError?: string | null;
}

type ItemState = 'pending' | 'active' | 'done' | 'error';

const ITEM_ICON: Record<ItemState, React.ReactNode> = {
    pending: <span className="size-2 rounded-full bg-muted-foreground/50" />,
    active: <Loader2 size={16} className="animate-spin text-primary" />,
    done: <Check size={16} className="text-success" />,
    error: <AlertCircle size={16} className="text-destructive" />,
};

function itemState(input: {
    isActive: boolean;
    isDone: boolean;
    hasError: boolean;
}): ItemState {
    if (input.isDone) return 'done';
    // the failing leg is tracked explicitly, so a late error can no longer be
    // painted onto whichever row a stale phase left active.
    if (input.hasError) return 'error';
    if (input.isActive) return 'active';
    return 'pending';
}

function phaseLabelFor(
    phase: EoaProvisioningPhase,
    anchorChainId: AnchorChainId,
): string {
    switch (phase) {
    case 'anchor-signing':
        return 'Confirm the two signature requests in your wallet.';
    case 'anchor-provisioning':
        return `Creating your authorization wallet on ${anchorLabel(anchorChainId)}.`;
    case 'hyperevm-signing':
        return 'Confirm the signature request in your wallet.';
    default:
        // the wait itself is the progress bar's line to carry, so the label
        // says what is happening and stops there.
        return 'Creating your HyperEVM trading wallet.';
    }
}

const DETACHED_LABEL = 'Creating your HyperEVM trading wallet.';

/**
 * Wallet-first account creation: two on-chain wallets, each authorized by a
 * signature from the connected owner. Both legs are idempotent, so retrying
 * after a failure resumes at the one that did not land.
 *
 * The HyperEVM leg finishes out of band on the backend, which means a failure
 * arrives through the admission queue rather than as a throw. Both routes end
 * here, with an action that actually moves the user forward.
 */
const ProvisionStep: React.FC<ProvisionStepProps> = ({
    phase,
    anchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
    failure,
    failedLeg,
    isRunning,
    hasStarted,
    detachedProvisioning,
    authorizationWalletReady,
    tradingWalletReady,
    socialProviderName = null,
    mismatch: liveMismatch = null,
    onStart,
    onSwitchAccount,
    onRejoin,
    onContinue,
    isContinuing = false,
    continueError = null,
}) => {
    const [isSwitching, setIsSwitching] = useState(false);
    const [switchError, setSwitchError] = useState<string | null>(null);
    // Signing out clears the login's owner, which makes the mismatch resolve
    // itself halfway through the switch. Holding the last one keeps the panel
    // up until the routing lands, rather than flashing an armed "set up my
    // account" button at a wallet that is on its way somewhere else.
    const [lastMismatch, setLastMismatch] = useState(liveMismatch);
    useEffect(() => {
        if (liveMismatch) {
            setLastMismatch(liveMismatch);
            return;
        }
        // the mismatch resolved, so the failure text from the last attempt to
        // leave it has nothing left to describe. Kept, it would reappear on the
        // next panel to mount, attached to a question nobody asked yet.
        setSwitchError(null);
    }, [liveMismatch]);
    const mismatch = liveMismatch ?? (isSwitching ? lastMismatch : null);

    const handleSwitch = async () => {
        setSwitchError(null);
        setIsSwitching(true);
        try {
            await onSwitchAccount();
        } catch (caught) {
            // the sign-out leg failed, so nothing was revoked: the user is
            // still on this account and can switch the wallet back instead.
            setSwitchError(describeErrorText(caught, { operation: 'Account switch' }));
        } finally {
            setIsSwitching(false);
        }
    };

    const onAnchor =
        phase === 'anchor-signing' || phase === 'anchor-provisioning';
    const onHyperEvm =
        phase === 'hyperevm-signing' ||
        phase === 'hyperevm-provisioning' ||
        detachedProvisioning;
    const items: { title: string; description: string; state: ItemState }[] = [
        {
            title: `Authorization wallet on ${anchorLabel(anchorChainId)}`,
            description: socialProviderName
                ? `Registers your connected wallet as the account owner, with your ${socialProviderName} login attached to it.`
                : 'Registers your connected wallet as the account owner.',
            state: itemState({
                isActive: onAnchor,
                isDone: authorizationWalletReady,
                hasError: failedLeg === 'anchor',
            }),
        },
        {
            title: 'HyperEVM trading wallet',
            description: 'Executes your trades without asking you to sign.',
            state: itemState({
                isActive: onHyperEvm,
                isDone: tradingWalletReady,
                hasError: failedLeg === 'hyperevm',
            }),
        },
    ];
    const isComplete = authorizationWalletReady && tradingWalletReady;
    // Work that is actually happening right now: this session's ceremony, or
    // the detached creation the backend is running. Anything derived from
    // "started at some point" is a memory, not a state, and must never be what
    // holds the only action on this step shut.
    const inProgress = isRunning || detachedProvisioning;
    // Nothing ran this session, yet the first wallet is already on-chain: the
    // user is coming back to a ceremony they left partway through.
    const isResuming =
        !isComplete &&
        !hasStarted &&
        !failure &&
        !detachedProvisioning &&
        authorizationWalletReady;
    // the association run signs once on the anchor and nothing at all on
    // HyperEVM, so the wallet-first label would over-promise the work.
    const phaseLabel =
        phase && socialProviderName && phase === 'anchor-signing'
            ? 'Confirm the request in your wallet.'
            : phase
                ? phaseLabelFor(phase, anchorChainId)
                : null;
    const progressLabel =
        phaseLabel && !failure
            ? phaseLabel
            : detachedProvisioning && !failure
                ? DETACHED_LABEL
                : null;

    return (
        <div className="flex flex-col items-center px-6 pb-6 pt-2 text-center">
            <div className="flex size-16 items-center justify-center rounded-2xl border border-border bg-secondary/50">
                <ShieldCheck size={30} className="text-primary" />
            </div>
            <h2 className="mt-4 text-xl font-bold tracking-tight text-foreground">
                Create your Snappy account
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
                {isResuming
                    ? 'Pick up where you left off. One signature request remains to finish your trading wallet.'
                    : socialProviderName
                        ? `Two wallets, created for you. You approve one request in your wallet to authorize them, and your ${socialProviderName} login is linked to the account.`
                        : 'Two wallets, created for you. You sign to authorize them, and pay no gas.'}
            </p>

            <div className="mt-6 w-full divide-y divide-border rounded-xl border border-border text-left">
                {items.map(({ title, description, state }) => (
                    <div key={title} className="flex items-center gap-3 p-3.5">
                        <div
                            className={cn(
                                'flex size-8 shrink-0 items-center justify-center rounded-lg border',
                                state === 'done'
                                    ? 'border-success/40 bg-success/10'
                                    : state === 'error'
                                        ? 'border-destructive/40 bg-destructive/10'
                                        : 'border-border bg-secondary/40',
                            )}
                        >
                            {ITEM_ICON[state]}
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground">
                                {title}
                            </p>
                            <p className="text-xs leading-relaxed text-muted-foreground">
                                {description}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {progressLabel && !isComplete && (
                <p className="mt-4 text-sm text-muted-foreground">
                    {progressLabel}
                </p>
            )}

            <ProvisioningProgress
                phase={phase}
                detached={detachedProvisioning}
                complete={isComplete}
                failed={!!failure || !!mismatch}
                className="mt-4"
            />

            {mismatch && !isComplete && (
                <div className="mt-4 w-full min-w-0 rounded-xl border border-destructive/40 bg-destructive/10 p-3.5 text-left [overflow-wrap:anywhere]">
                    <p
                        role="alert"
                        className="flex items-center gap-2 text-sm font-semibold text-foreground"
                    >
                        <ArrowLeftRight className="size-4 shrink-0 text-destructive" />
                        Your wallet is on a different account
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                        You signed in as{' '}
                        <span
                            className="font-mono text-foreground"
                            title={mismatch.authenticatedOwner}
                        >
                            {shortenAddress(mismatch.authenticatedOwner)}
                        </span>
                        , and your wallet now points at{' '}
                        <span
                            className="font-mono text-foreground"
                            title={mismatch.connectedAddress}
                        >
                            {shortenAddress(mismatch.connectedAddress)}
                        </span>
                        . Your account has to be created by the wallet you
                        signed in with, so nothing was signed.
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                        Switch back to{' '}
                        <span
                            className="font-mono text-foreground"
                            title={mismatch.authenticatedOwner}
                        >
                            {shortenAddress(mismatch.authenticatedOwner)}
                        </span>{' '}
                        in your wallet and setup picks up here on its own.
                    </p>
                    {switchError && (
                        <p className="mt-2 text-xs text-destructive">
                            {switchError}
                        </p>
                    )}
                    <Button
                        variant="secondary"
                        className="mt-3 w-full"
                        disabled={isSwitching}
                        title={mismatch.connectedAddress}
                        onClick={() => void handleSwitch()}
                    >
                        {isSwitching && <Loader2 className="animate-spin" />}
                        Sign out and continue with{' '}
                        {shortenAddress(mismatch.connectedAddress)}
                    </Button>
                </div>
            )}

            {failure && !mismatch && !isComplete && (
                <div className="mt-4 min-w-0 [overflow-wrap:anywhere]">
                    <p role="alert" className="text-sm text-destructive">
                        {failure.title}
                    </p>
                    {failure.reason && (
                        <p className="mt-1 text-xs text-muted-foreground">
                            {failure.reason}
                        </p>
                    )}
                </div>
            )}

            {isComplete ? (
                <>
                    {continueError && !isContinuing && (
                        <div className="mt-4 min-w-0 [overflow-wrap:anywhere]">
                            <p role="alert" className="text-sm text-destructive">
                                {continueError}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                Press Continue to funding to try again.
                            </p>
                        </div>
                    )}
                    <Button
                        className="mt-5 w-full"
                        disabled={isContinuing}
                        onClick={onContinue}
                    >
                        {isContinuing && <Loader2 className="animate-spin" />}
                        {isContinuing
                            ? 'Confirm the signature request in your wallet'
                            : 'Continue to funding'}
                    </Button>
                </>
            ) : mismatch ? null : failure?.kind === 'rejoin' ? (
                <Button className="mt-5 w-full" onClick={onRejoin}>
                    Rejoin the line
                </Button>
            ) : (
                <Button
                    className="mt-5 w-full"
                    // Only live work disables this. `hasStarted` used to as
                    // well, and it survives its own ceremony: a run that ended
                    // in a lost lane left the button disabled and labelled as
                    // busy with nothing running behind it, for good.
                    disabled={inProgress}
                    onClick={onStart}
                >
                    {isRunning && <Loader2 className="animate-spin" />}
                    {failure
                        ? 'Try again'
                        : inProgress
                            ? 'Setting up your account…'
                            : isResuming
                                ? 'Finish setting up my account'
                                : hasStarted
                                    ? 'Continue setup'
                                    : 'Set up my account'}
                </Button>
            )}
        </div>
    );
};

export default ProvisionStep;
